import styled from "@emotion/styled";
import React from "react";
import {
  Link,
  Navigate,
  Outlet,
  RouteProps,
  useParams
} from "react-router-dom";
import { observer } from "mobx-react-lite";
import { useStores } from "@stores";
import Layout from "@components/Layout";
import { MARKETS, POOLS, ROME_ASSET_ID, ROUTES } from "@src/constants";
import {
  MarketPageVMProvider,
  useMarketPageVM
} from "@screens/MarketPage/MarketPageVm";
import { Row } from "@components/Flex";
import Text from "@components/Text";
import SizedBox from "@components/SizedBox";
import FAQ from "@screens/MarketPage/FAQ";
import { ReactComponent as ArrowBackIcon } from "@src/assets/icons/backArrow.svg";
import RomeAssetsTable from "@screens/MarketPage/RomeMarket/RomeAssetsTable";
import RomeMarketInfo from "@screens/MarketPage/RomeMarket/RomeMarketInfo";
import AccountCard from "@screens/MarketPage/AccountCard";
import RomeAccountSupplyAndBorrow from "@screens/MarketPage/RomeMarket/RomeAccountSupplyAndBorrow";

interface IProps {}

const Root = styled.div<{ apySort?: boolean; liquiditySort?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-sizing: border-box;
  padding: 0 16px;
  width: 100%;
  min-height: 100%;
  max-width: calc(1328px + 32px);
  margin-bottom: 72px;
  margin-top: 40px;
  text-align: left;
`;
const RomeMarketImpl: React.FC<IProps> = observer(() => {
  const vm = useMarketPageVM();
  const { lendStore } = useStores();
  lendStore.setPool(POOLS.find((p) => p.address === ROME_ASSET_ID)!);
  const market = MARKETS.find((m) => m.address === ROME_ASSET_ID);
  if (!market) return <Navigate to={ROUTES.ROOT} />;

  return (
    <Layout>
      <Root apySort={vm.sortApy} liquiditySort={vm.sortLiquidity}>
        <Link to={ROUTES.ROOT}>
          <Row alignItems="center">
            <ArrowBackIcon />
            <Text weight={500} type="blue500">
              Back to Dashboard
            </Text>
          </Row>
        </Link>
        <SizedBox height={16} />
        <RomeMarketInfo market={market} />
        <AccountCard market={market} />
        <RomeAccountSupplyAndBorrow />
        <RomeAssetsTable />
        <SizedBox height={40} />
        <FAQ />
        <Outlet />
      </Root>
    </Layout>
  );
});

const RomeMarket: React.FC<IProps & RouteProps> = () => {
  const params = useParams<{ marketId: string }>();

  if (params.marketId && !POOLS.some((p) => p.address === params.marketId)) {
    return <Navigate to={ROUTES.ROOT} />;
  }
  return (
    <MarketPageVMProvider marketId={params.marketId}>
      <RomeMarketImpl />
    </MarketPageVMProvider>
  );
};

export default observer(RomeMarket);
